import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import { toast } from 'sonner';
import { isAbortError } from '../lib/utils';
import { deriveQuadrant } from '../components/tasks/taskMeta';

// ═══════════════════════════════════════════════════════════════════════════════
// QUERY KEYS - Centralized key factory for consistent cache management
// ═══════════════════════════════════════════════════════════════════════════════

export const projectKeys = {
    all: ['projects'],
    lists: () => [...projectKeys.all, 'list'],
    list: (filters) => [...projectKeys.lists(), filters],
    details: () => [...projectKeys.all, 'detail'],
    detail: (id) => [...projectKeys.details(), id],
    files: (id) => [...projectKeys.detail(id), 'files'],
    tasks: (id) => [...projectKeys.detail(id), 'tasks'],
    dashboard: (id) => [...projectKeys.detail(id), 'dashboard'],
    chatSessions: (id) => [...projectKeys.detail(id), 'chat-sessions'],
    chatSession: (id, sessionId) => [...projectKeys.chatSessions(id), sessionId],
};

export const taskChangeKeys = {
    all: (projectId) => [...projectKeys.detail(projectId), 'task-changes'],
    session: (projectId, sessionId) => [...taskChangeKeys.all(projectId), sessionId || 'none'],
};

export const dashboardKeys = {
    all: ['dashboard'],
    main: () => [...dashboardKeys.all, 'main'],
};

export const modelKeys = {
    all: ['models'],
};

// ═══════════════════════════════════════════════════════════════════════════════
// DASHBOARD QUERIES
// ═══════════════════════════════════════════════════════════════════════════════

export function useDashboard() {
    return useQuery({
        queryKey: dashboardKeys.main(),
        queryFn: () => api.get('/dashboard')
            .then(res => res.data)
            .catch(err => {
                console.error('[Dashboard Query] Failed:', err.response?.status, err.response?.data);
                throw err;
            }),
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROJECT QUERIES
// ═══════════════════════════════════════════════════════════════════════════════

export function useProject(projectId, token = null) {
    return useQuery({
        queryKey: token ? ['shared', token] : projectKeys.detail(projectId),
        queryFn: () => token
            ? api.get(`/shared/${token}`).then(res => res.data.project)
            : api.get(`/projects/${projectId}`).then(res => res.data),
        enabled: !!projectId || !!token,
    });
}

export function useProjectFiles(projectId, token = null) {
    return useQuery({
        queryKey: token ? ['shared', token, 'files'] : projectKeys.files(projectId),
        queryFn: () => token
            ? api.get(`/shared/${token}/files`).then(res => res.data)
            : api.get(`/projects/${projectId}/files`).then(res => res.data),
        enabled: !!projectId || !!token,
    });
}

export function useProjectTasks(projectId, token = null) {
    return useQuery({
        queryKey: token ? ['shared', token, 'tasks'] : projectKeys.tasks(projectId),
        queryFn: () => token
            ? api.get(`/shared/${token}/tasks`).then(res => res.data)
            : api.get(`/projects/${projectId}/tasks`).then(res => res.data),
        enabled: !!projectId || !!token,
    });
}

export function useProjectDashboard(projectId) {
    return useQuery({
        queryKey: projectKeys.dashboard(projectId),
        queryFn: () => api.get(`/projects/${projectId}/dashboard`).then(res => res.data),
        enabled: !!projectId,
    });
}

export function useChatSessions(projectId) {
    return useQuery({
        queryKey: projectKeys.chatSessions(projectId),
        queryFn: () => api.get(`/projects/${projectId}/chat-sessions`).then(res => res.data),
        enabled: !!projectId,
    });
}

export function useChatSession(sessionId) {
    return useQuery({
        queryKey: ['chat-session', sessionId],
        queryFn: () => api.get(`/chat-sessions/${sessionId}`).then(res => res.data),
        enabled: !!sessionId,
    });
}

export function useModels() {
    return useQuery({
        queryKey: modelKeys.all,
        queryFn: () => api.get('/models').then(res => res.data),
        staleTime: 5 * 60 * 1000, // Models rarely change, cache for 5 min
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROJECT MUTATIONS
// ═══════════════════════════════════════════════════════════════════════════════

export function useCreateProject() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data) => api.post('/projects', data).then(res => res.data),
        onSuccess: (newProject) => {
            queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
        },
    });
}

/**
 * Update a project's own fields (name, icon, links, tags, …).
 *
 * Settings used to PUT directly with axios and keep the response in local
 * state, which left `projectKeys.detail` stale — so editing links there did not
 * change the console's top bar until a full reload. Everything that reads the
 * project goes through this cache, so the write has to go through it too.
 *
 * The backend allowlist is name / status / tags / links / icon /
 * custom_categories / collaborators; anything else is dropped server-side.
 */
export function useUpdateProject(projectId) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (updates) => api.put(`/projects/${projectId}`, updates).then(res => res.data),
        onSuccess: (updated) => {
            // Seed the detail cache from the response rather than only
            // invalidating, so the bar re-renders in the same tick as the toast.
            if (updated) queryClient.setQueryData(projectKeys.detail(projectId), updated);
            queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
            queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
        },
    });
}

export function useDeleteProject() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (projectId) => api.delete(`/projects/${projectId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
        },
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// TASK MUTATIONS (with optimistic updates)
// ═══════════════════════════════════════════════════════════════════════════════

// Task writes made from inside a chat carry the session id so they land in that
// conversation's change log and the agent can see them on its next turn.
const sessionParams = (sessionId) => (sessionId ? { params: { session_id: sessionId } } : undefined);

export function useCreateTask(projectId, sessionId = null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (taskData) =>
            api.post('/tasks', { project_id: projectId, ...taskData }, sessionParams(sessionId)).then(res => res.data),
        onMutate: async (newTask) => {
            await queryClient.cancelQueries({ queryKey: projectKeys.tasks(projectId) });
            const previousTasks = queryClient.getQueryData(projectKeys.tasks(projectId));

            // Optimistically add the new task
            const optimisticTask = {
                id: `temp-${Date.now()}`,
                project_id: projectId,
                status: 'todo',
                priority: 'medium',
                importance: 'medium',
                quadrant: 'q2',
                created_at: new Date().toISOString(),
                ...newTask,
            };

            queryClient.setQueryData(projectKeys.tasks(projectId), old =>
                old ? [optimisticTask, ...old] : [optimisticTask]
            );

            return { previousTasks };
        },
        onError: (err, newTask, context) => {
            queryClient.setQueryData(projectKeys.tasks(projectId), context.previousTasks);
            toast.error("Failed to create task");
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.tasks(projectId) });
            queryClient.invalidateQueries({ queryKey: taskChangeKeys.all(projectId) });
            queryClient.invalidateQueries({ queryKey: projectKeys.dashboard(projectId) });
        },
    });
}

export function useUpdateTask(projectId, sessionId = null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, updates }) => api.put(`/tasks/${id}`, updates, sessionParams(sessionId)),
        onMutate: async ({ id, updates }) => {
            await queryClient.cancelQueries({ queryKey: projectKeys.tasks(projectId) });
            const previousTasks = queryClient.getQueryData(projectKeys.tasks(projectId));

            // Auto-calculate quadrant when priority or importance changes.
            // `deriveQuadrant` rather than the rule spelled out inline: it was
            // written out a fourth time here, and the copy the server applies
            // is the one that wins a beat later anyway.
            let finalUpdates = { ...updates };
            const task = previousTasks?.find(t => t.id === id);
            if (task && (updates.priority !== undefined || updates.importance !== undefined)) {
                finalUpdates.quadrant = deriveQuadrant(
                    updates.priority || task.priority,
                    updates.importance || task.importance,
                );
            }

            // Same shape of problem as the quadrant above: the wire format and
            // the stored field differ. We send `demoted` and the server stamps
            // `demoted_at` from its own clock — but every reader in the app
            // tests `demoted_at`, so writing the boolean into the cache
            // optimistically updates a field nobody looks at, and the card you
            // just demoted stays on the board until the round trip lands. The
            // server's timestamp replaces this one on invalidation.
            if (updates.demoted !== undefined) {
                finalUpdates.demoted_at = updates.demoted ? new Date().toISOString() : null;
                delete finalUpdates.demoted;
            }

            queryClient.setQueryData(projectKeys.tasks(projectId), old =>
                old?.map(t => t.id === id ? { ...t, ...finalUpdates } : t)
            );

            return { previousTasks };
        },
        onError: (err, variables, context) => {
            queryClient.setQueryData(projectKeys.tasks(projectId), context.previousTasks);
            toast.error("Update failed");
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: taskChangeKeys.all(projectId) });
            // Without this the optimistic value from onMutate became permanent
            // client-side truth and every server-side derivation was lost:
            // due_date coercion, the re-derived quadrant, updated_at,
            // last_touched_by. A rename whose new text resolves a date on the
            // server would never have shown it.
            queryClient.invalidateQueries({ queryKey: projectKeys.tasks(projectId) });
            queryClient.invalidateQueries({ queryKey: projectKeys.dashboard(projectId) });
        },
    });
}

export function useDeleteTask(projectId, sessionId = null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (taskId) => api.delete(`/tasks/${taskId}`, sessionParams(sessionId)),
        onMutate: async (taskId) => {
            await queryClient.cancelQueries({ queryKey: projectKeys.tasks(projectId) });
            const previousTasks = queryClient.getQueryData(projectKeys.tasks(projectId));

            queryClient.setQueryData(projectKeys.tasks(projectId), old =>
                old?.filter(t => t.id !== taskId)
            );

            return { previousTasks };
        },
        onError: (err, taskId, context) => {
            queryClient.setQueryData(projectKeys.tasks(projectId), context.previousTasks);
            toast.error("Failed to delete task");
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: taskChangeKeys.all(projectId) });
            queryClient.invalidateQueries({ queryKey: projectKeys.tasks(projectId) });
            queryClient.invalidateQueries({ queryKey: projectKeys.dashboard(projectId) });
            toast.success("Task deleted");
        },
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// TASK CHANGE LOG - agent changesets + undo
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Every task write in a chat session, grouped into per-turn changesets.
 * This is what the agent task panel renders, and it is durable - reloading the
 * page still lets you revert something the agent did an hour ago.
 */
export function useTaskChangesets(projectId, sessionId, { enabled = true } = {}) {
    return useQuery({
        queryKey: taskChangeKeys.session(projectId, sessionId),
        queryFn: () => api
            .get(`/projects/${projectId}/task-changes`, { params: { session_id: sessionId } })
            .then(res => res.data.batches || []),
        enabled: !!projectId && !!sessionId && enabled,
        staleTime: 10 * 1000,
    });
}

/**
 * Publish the changesets a chat turn just produced, straight from the reply.
 *
 * Task tools run server-side inside the turn now, so by the time the reply
 * arrives the writes are already committed and the response is carrying the
 * change rows. Waiting for the changesets query to refetch before showing them
 * would leave the panel rendering its "No agent activity yet" empty state for a
 * whole round trip — telling the user nothing happened at the moment something
 * did. So the rows go into the cache synchronously and the refetch behind them
 * only ever corrects revert and conflict state.
 *
 * Returns how many changes were published, so the caller can decide what to say.
 */
export function useSeedTaskChangesets(projectId, sessionId) {
    const queryClient = useQueryClient();

    return useCallback((taskBatches) => {
        const batches = (taskBatches || []).filter(b => b?.batch_id);
        if (!batches.length) return 0;

        const seeded = batches.map(batch => {
            const changes = batch.changes || [];
            return {
                batch_id: batch.batch_id,
                session_id: sessionId ?? null,
                message_index: batch.message_index ?? null,
                source: 'ai',
                created_at: changes[0]?.created_at || new Date().toISOString(),
                changes,
                summary: batch.summary || { created: 0, updated: 0, deleted: 0 },
                total: changes.length,
                // Nothing can have been reverted yet, and no change made a moment
                // ago can conflict — the refetch corrects both if that's wrong.
                active: changes.length,
                conflicted: false,
            };
        });

        const key = taskChangeKeys.session(projectId, sessionId);
        const seededIds = new Set(seeded.map(b => b.batch_id));
        // Newest first, matching the server's ordering. Filtering by id keeps
        // this idempotent: the refetch returns these same batches, and without
        // the dedupe each one would flash twice.
        queryClient.setQueryData(key, (old = []) => [
            ...seeded,
            ...(old || []).filter(b => !seededIds.has(b.batch_id)),
        ]);

        queryClient.invalidateQueries({ queryKey: key });
        // The board and the home counters read these; the agent's writes are the
        // one path that must never leave them stale.
        queryClient.invalidateQueries({ queryKey: projectKeys.tasks(projectId) });
        queryClient.invalidateQueries({ queryKey: projectKeys.dashboard(projectId) });

        return seeded.reduce((n, b) => n + b.total, 0);
    }, [queryClient, projectId, sessionId]);
}

/** Shared success path for both revert flavours. */
function applyRevertResult(queryClient, projectId, sessionId, data) {
    queryClient.invalidateQueries({ queryKey: projectKeys.tasks(projectId) });
    queryClient.invalidateQueries({ queryKey: taskChangeKeys.session(projectId, sessionId) });
    queryClient.invalidateQueries({ queryKey: projectKeys.dashboard(projectId) });

    if (data?.conflicts?.length) {
        toast.warning('Some changes were edited since', {
            description: 'Confirm again to revert them anyway.',
        });
    }
    return data;
}

export function useRevertTaskChange(projectId, sessionId) {
    const queryClient = useQueryClient();

    return useMutation({
        // The session travels with the revert so the server can attribute the
        // companion `revert_of` row to this conversation — that row is how the
        // agent learns, on your next message, that you undid something.
        mutationFn: ({ changeId, force = false }) =>
            api.post(`/task-changes/${changeId}/revert`, { force, session_id: sessionId ?? null }).then(res => res.data),
        onSuccess: (data) => applyRevertResult(queryClient, projectId, sessionId, data),
        onError: (error) => {
            toast.error('Revert failed', {
                description: error.response?.data?.detail || error.message,
            });
        },
    });
}

export function useRevertTaskBatch(projectId, sessionId) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ batchId, force = false }) =>
            api.post(`/task-changes/batch/${batchId}/revert`, { force, session_id: sessionId ?? null }).then(res => res.data),
        onSuccess: (data) => applyRevertResult(queryClient, projectId, sessionId, data),
        onError: (error) => {
            toast.error('Revert failed', {
                description: error.response?.data?.detail || error.message,
            });
        },
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// FILE MUTATIONS (with optimistic updates)
// ═══════════════════════════════════════════════════════════════════════════════

export function useCreateFile(projectId) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (fileData) => api.post('/files', { project_id: projectId, ...fileData }).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.files(projectId) });
        },
    });
}

export function useUpdateFile(projectId) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, updates }) => api.put(`/files/${id}`, updates),
        onMutate: async ({ id, updates }) => {
            await queryClient.cancelQueries({ queryKey: projectKeys.files(projectId) });
            const previousFiles = queryClient.getQueryData(projectKeys.files(projectId));

            queryClient.setQueryData(projectKeys.files(projectId), old =>
                old?.map(f => f.id === id ? { ...f, ...updates } : f)
            );

            return { previousFiles };
        },
        onError: (err, variables, context) => {
            queryClient.setQueryData(projectKeys.files(projectId), context.previousFiles);
            toast.error("Update failed");
        },
    });
}

export function useDeleteFile(projectId) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (fileId) => api.delete(`/files/${fileId}`),
        onMutate: async (fileId) => {
            await queryClient.cancelQueries({ queryKey: projectKeys.files(projectId) });
            const previousFiles = queryClient.getQueryData(projectKeys.files(projectId));

            queryClient.setQueryData(projectKeys.files(projectId), old =>
                old?.filter(f => f.id !== fileId)
            );

            return { previousFiles };
        },
        onError: (err, fileId, context) => {
            queryClient.setQueryData(projectKeys.files(projectId), context.previousFiles);
            toast.error("Failed to delete file");
        },
        onSuccess: () => {
            toast.success("File deleted");
        },
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// CHAT SESSION MUTATIONS
// ═══════════════════════════════════════════════════════════════════════════════

export function useCreateChatSession(projectId) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => api.post(`/projects/${projectId}/chat-sessions`).then(res => res.data),
        onSuccess: (newSession) => {
            queryClient.setQueryData(projectKeys.chatSessions(projectId), old =>
                old ? [newSession, ...old] : [newSession]
            );
        },
    });
}

export function useDeleteChatSession(projectId) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (sessionId) => api.delete(`/chat-sessions/${sessionId}`),
        onMutate: async (sessionId) => {
            await queryClient.cancelQueries({ queryKey: projectKeys.chatSessions(projectId) });
            const previousSessions = queryClient.getQueryData(projectKeys.chatSessions(projectId));

            queryClient.setQueryData(projectKeys.chatSessions(projectId), old =>
                old?.filter(s => s.id !== sessionId)
            );

            return { previousSessions };
        },
        onError: (err, sessionId, context) => {
            queryClient.setQueryData(projectKeys.chatSessions(projectId), context.previousSessions);
            toast.error("Failed to delete conversation");
        },
        onSuccess: () => {
            toast.success("Conversation deleted");
        },
    });
}

export function useUpdateChatSession(projectId) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ sessionId, updates }) => api.put(`/chat-sessions/${sessionId}`, updates),
        onMutate: async ({ sessionId, updates }) => {
            await queryClient.cancelQueries({ queryKey: projectKeys.chatSessions(projectId) });
            const previousSessions = queryClient.getQueryData(projectKeys.chatSessions(projectId));

            queryClient.setQueryData(projectKeys.chatSessions(projectId), old =>
                old?.map(s => s.id === sessionId ? { ...s, ...updates } : s)
                    .sort((a, b) => {
                        if (a.pinned && !b.pinned) return -1;
                        if (!a.pinned && b.pinned) return 1;
                        return new Date(b.updated_at) - new Date(a.updated_at);
                    })
            );

            return { previousSessions };
        },
        onError: (err, variables, context) => {
            queryClient.setQueryData(projectKeys.chatSessions(projectId), context.previousSessions);
        },
    });
}

/**
 * Drop every persisted message from `fromIndex` onward.
 *
 * Retry uses this to genuinely replace an answer instead of appending a second
 * one, and a failed send uses it to clear a prompt the server may or may not
 * have got far enough to store. Deliberately has no optimistic update: the
 * caller owns the local `messages` array and only trims it once the server has
 * confirmed, so a failed truncate can never leave the UI showing less than the
 * database holds.
 */
export function useTruncateChatMessages(projectId) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ sessionId, fromIndex }) =>
            api.post(`/chat-sessions/${sessionId}/messages/truncate`, { from_index: fromIndex })
                .then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.chatSessions(projectId) });
        },
    });
}

export function useBranchChatSession(projectId) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ sessionId, fromUserIndex }) =>
            api.post(`/chat-sessions/${sessionId}/branch`, { from_user_index: fromUserIndex })
                .then(res => res.data),
        onSuccess: (newSession) => {
            const listEntry = {
                id: newSession.id,
                project_id: newSession.project_id,
                title: newSession.title,
                message_count: newSession.messages?.length ?? 0,
                pinned: newSession.pinned ?? false,
                created_at: newSession.created_at,
                updated_at: newSession.updated_at,
            };
            queryClient.setQueryData(projectKeys.chatSessions(projectId), old => {
                if (!old) return [listEntry];
                const filtered = old.filter(s => s.id !== listEntry.id);
                return [listEntry, ...filtered].sort((a, b) => {
                    if (a.pinned && !b.pinned) return -1;
                    if (!a.pinned && b.pinned) return 1;
                    return new Date(b.updated_at) - new Date(a.updated_at);
                });
            });
            queryClient.invalidateQueries({ queryKey: projectKeys.chatSessions(projectId) });
        },
    });
}

export function useSendChatMessage(projectId) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ sessionId, message, contextMode, referencedFiles, referencedTasks, attachedImages, webSearch, modelPreset, agenticMode = true, signal }) =>
            api.post(`/chat-sessions/${sessionId}/messages`, {
                message,
                context_mode: contextMode,
                referenced_files: referencedFiles,
                referenced_tasks: referencedTasks,
                attached_images: attachedImages || [],
                web_search: webSearch,
                model_preset: modelPreset,
                agentic_mode: agenticMode,
            }, { signal }).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.chatSessions(projectId) });
        },
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// AI TOOL EXECUTION
// ═══════════════════════════════════════════════════════════════════════════════

export function useExecuteToolCall(projectId) {
    const queryClient = useQueryClient();

    const TASK_TOOLS = ['create_tasks', 'update_tasks', 'delete_tasks', 'modify_task'];

    return useMutation({
        mutationFn: ({ toolName, arguments: args, sessionId = null, messageIndex = null, signal }) =>
            api.post('/ai/execute-tool', {
                tool_name: toolName,
                arguments: args,
                project_id: projectId,
                session_id: sessionId,
                message_index: messageIndex,
            }, { signal }).then(res => res.data),
        onSuccess: (result, variables) => {
            // Invalidate relevant queries based on tool type
            if (result.tool_name === 'create_document' || result.tool_name === 'modify_document') {
                queryClient.invalidateQueries({ queryKey: projectKeys.files(projectId) });
            }
            if (TASK_TOOLS.includes(result.tool_name)) {
                queryClient.invalidateQueries({ queryKey: projectKeys.tasks(projectId) });
                queryClient.invalidateQueries({ queryKey: projectKeys.dashboard(projectId) });
                queryClient.invalidateQueries({
                    queryKey: taskChangeKeys.session(projectId, variables?.sessionId),
                });
            }
        },
        onError: (error) => {
            // A user-initiated stop is not a failure worth shouting about.
            if (isAbortError(error)) return;
            toast.error(`Tool execution failed: ${error.response?.data?.detail || error.message}`);
        },
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// AI DOCUMENT EDITING - Multi-step editing with diff preview
// ═══════════════════════════════════════════════════════════════════════════════

export function useEditDocument(projectId) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ toolName, fileId, fileName, instructions, signal }) =>
            api.post('/ai/edit-document', {
                tool_name: toolName,
                file_id: fileId,
                file_name: fileName,
                instructions: instructions,
                project_id: projectId,
            }, { signal }).then(res => res.data),
        onSuccess: () => {
            // Don't invalidate yet - wait for user to accept changes
            // Invalidation happens when user accepts the diff
        },
        onError: (error) => {
            if (isAbortError(error)) return;
            toast.error(`Document edit failed: ${error.response?.data?.detail || error.message}`);
        },
    });
}

// Helper to save accepted changes after diff review
export function useAcceptDocumentChanges(projectId) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ fileId, newContent }) =>
            api.put(`/files/${fileId}`, { content: newContent }).then(res => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.files(projectId) });
            toast.success('Changes applied successfully');
        },
        onError: (error) => {
            toast.error(`Failed to save changes: ${error.response?.data?.detail || error.message}`);
        },
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// INVITATIONS - Accept/Decline project invites
// ═══════════════════════════════════════════════════════════════════════════════

export function useAcceptInvite() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (token) =>
            api.post(`/invites/${token}/accept`).then(res => res.data),
        onSuccess: (data, token) => {
            // Remove from dashboard cache immediately for delightful UI removal
            queryClient.setQueryData(dashboardKeys.main(), (old) => {
                if (!old || !old.invites) return old;
                return {
                    ...old,
                    invites: old.invites.filter(i => i.token !== token)
                };
            });

            queryClient.invalidateQueries({ queryKey: dashboardKeys.main() });
            queryClient.invalidateQueries({ queryKey: projectKeys.all });
            toast.success('Joined project successfully');
        },
        onError: (error) => {
            toast.error(`Failed to accept invite: ${error.response?.data?.detail || error.message}`);
        },
    });
}

export function useDeclineInvite() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (token) =>
            api.post(`/invites/${token}/decline`).then(res => res.data),
        onSuccess: (data, token) => {
            // Remove from dashboard cache immediately for delightful UI removal
            queryClient.setQueryData(dashboardKeys.main(), (old) => {
                if (!old || !old.invites) return old;
                return {
                    ...old,
                    invites: old.invites.filter(i => i.token !== token)
                };
            });

            queryClient.invalidateQueries({ queryKey: dashboardKeys.main() });
            toast.success('Invite declined');
        },
        onError: (error) => {
            toast.error(`Failed to decline invite: ${error.response?.data?.detail || error.message}`);
        },
    });
}
